export function isImageFormat(f) {
    return f === 'jpeg' || f === 'png';
}
export function isVideoFormat(f) {
    return f === 'mp4' || f === 'avi';
}
/**
 * MIME type for a produced file's extension, used when a single exported file is
 * saved directly (multi-file exports are zipped). Unknown extensions fall back
 * to `application/octet-stream`.
 */
export function mimeForExt(ext) {
    switch (ext.toLowerCase()) {
        case 'dcm':
            return 'application/dicom';
        case 'jpg':
        case 'jpeg':
            return 'image/jpeg';
        case 'png':
            return 'image/png';
        case 'mp4':
            return 'video/mp4';
        case 'avi':
            return 'video/x-msvideo';
        case 'pdf':
            return 'application/pdf';
        default:
            return 'application/octet-stream';
    }
}
/**
 * Error raised by the export pipeline that carries an i18n key (and optional
 * interpolation params) instead of a baked-in English message, so the dialog
 * can surface a translated description. `message` is set to the key as a
 * non-localized fallback for logs.
 */
export class DownloadError extends Error {
    i18nKey;
    i18nParams;
    constructor(i18nKey, i18nParams) {
        super(i18nKey);
        this.i18nKey = i18nKey;
        this.i18nParams = i18nParams;
        this.name = 'DownloadError';
    }
}
/** True when a series can be exported as a cine/video clip. */
export function isCineSeries(series) {
    if (series.images.some((img) => img.isVideo))
        return true;
    if ((series.numberOfFrames ?? 0) > 1)
        return true;
    if (series.images.some((img) => (img.numberOfFrames ?? 0) > 1))
        return true;
    return false;
}
/** True when a series has at least one rasterizable (non-encapsulated-video) image. */
function hasRasterImages(series) {
    return series.images.some((img) => !img.isVideo);
}
/** Series that yield a video clip for the given selection. Pure (no heavy deps). */
export function cineSeriesFor(study, seriesIds) {
    const wanted = new Set(seriesIds);
    return study.series.filter((s) => wanted.has(s.id) && isCineSeries(s));
}
/**
 * The clips a cine series exports as. One clip per loop, never one per series:
 * an ultrasound machine files a whole exam as ONE series with every loop a
 * multi-frame instance inside it, and concatenating them made one video of
 * every loop back to back, named after the series.
 *
 * - Each encapsulated video instance is its own clip.
 * - Each multi-frame raster instance is its own clip. Single-frame stills that
 *   sit beside loops in the same series are left to the JPEG/PNG export — a
 *   still is not a clip.
 * - A series with neither (a stack of single-frame instances that the series
 *   header calls a cine) plays as one sequence, so it is ONE clip of them all.
 */
export function cineClipsForSeries(series) {
    if (!isCineSeries(series))
        return [];
    const ordered = series.images.toSorted((a, b) => (a.instanceNumber ?? 0) - (b.instanceNumber ?? 0));
    const clips = [];
    for (const image of ordered) {
        if (image.isVideo) {
            clips.push({ series, kind: 'video', images: [image], instance: image });
        }
        else if ((image.numberOfFrames ?? 0) > 1) {
            clips.push({ series, kind: 'raster', images: [image], instance: image });
        }
    }
    if (clips.length > 0)
        return clips;
    const stack = ordered.filter((image) => !image.isVideo);
    return stack.length > 0 ? [{ series, kind: 'raster', images: stack }] : [];
}
/** Every clip of the selected cine series, in study order. Pure (no heavy deps). */
export function cineClipsFor(study, seriesIds) {
    return cineSeriesFor(study, seriesIds).flatMap(cineClipsForSeries);
}
/** The clip of `series` that holds the instance `imageUid`, or null when none does. */
export function cineClipForInstance(series, imageUid) {
    return (cineClipsForSeries(series).find((clip) => clip.images.some((i) => i.imageUid === imageUid)) ??
        null);
}
/** Image-instance count above which a client-side export is flagged as large. */
export const LARGE_EXPORT_IMAGE_COUNT = 750;
/**
 * Frames above which an archive is refused where it would have to be built in
 * memory (no File System Access API: Safari, Firefox). At roughly half a
 * megabyte a frame that is about a gigabyte, which an object-URL download can
 * still carry; the 12,753-image CT that hung a tab at "Packaging archive" was
 * several times that. Chrome and Edge stream to disk and have no such ceiling.
 */
export const IN_MEMORY_MAX_FRAMES = 2_000;
/** Frames across the selected series of every selection (instances × frames). */
export function exportFrameCount(selections) {
    let frames = 0;
    for (const sel of selections) {
        const wanted = new Set(sel.seriesIds);
        for (const series of sel.study.series) {
            if (!wanted.has(series.id))
                continue;
            for (const image of series.images)
                frames += Math.max(1, image.numberOfFrames ?? 1);
        }
    }
    return frames;
}
/**
 * Whether the export will be an archive (several files) rather than one file
 * saved as-is — decided before anything is fetched, so the save picker can be
 * asked for the archive's name while the click's user gesture is still fresh.
 * Mirrors runDownload: one produced file for one study saves directly.
 */
export function willArchive(selections, options) {
    if (selections.length > 1)
        return true;
    const [sel] = selections;
    if (!sel)
        return false;
    switch (options.format) {
        case 'pdf':
            return sel.reportIds.length > 1;
        case 'mp4':
        case 'avi':
            // One file per clip (loop), not per series — see cineClipsForSeries.
            return cineClipsFor(sel.study, sel.seriesIds).length > 1;
        case 'jpeg':
        case 'png':
            // Every frame of a cine series becomes a file of its own.
            return exportFrameCount([sel]) > 1;
        case 'dicom': {
            const wanted = new Set(sel.seriesIds);
            let instances = 0;
            for (const series of sel.study.series) {
                if (wanted.has(series.id))
                    instances += series.images.length;
            }
            return instances > 1;
        }
    }
}
/**
 * Estimate an export's size from the selection, without fetching anything. Pure.
 * PDF exports (reports) are never flagged — they're small regardless of count.
 */
export function estimateExport(selections, options) {
    if (options.format === 'pdf') {
        const reportCount = selections.reduce((n, s) => n + s.reportIds.length, 0);
        return { imageCount: 0, frameCount: 0, seriesCount: 0, reportCount, isLarge: false };
    }
    let imageCount = 0;
    let seriesCount = 0;
    for (const sel of selections) {
        const wanted = new Set(sel.seriesIds);
        for (const series of sel.study.series) {
            if (!wanted.has(series.id))
                continue;
            seriesCount++;
            imageCount += series.images.length;
        }
    }
    return {
        imageCount,
        frameCount: exportFrameCount(selections),
        seriesCount,
        reportCount: 0,
        isLarge: imageCount >= LARGE_EXPORT_IMAGE_COUNT,
    };
}
const FORMAT_ORDER = ['dicom', 'jpeg', 'png', 'mp4', 'avi', 'pdf'];
/**
 * Compute the set of export formats that make sense for a study's actual
 * content, so the dialog only offers producible formats:
 *  - DICOM whenever the study has any instances.
 *  - JPEG/PNG whenever any series has rasterizable still frames.
 *  - MP4/AVI whenever any series is a cine/video clip.
 *  - PDF whenever the study has at least one report.
 *
 * The returned list is in stable display order.
 */
export function applicableFormats(study, reportCount) {
    const set = new Set();
    const anyImages = study.series.some((s) => s.images.length > 0);
    const anyRaster = study.series.some((s) => hasRasterImages(s));
    const anyCine = study.series.some((s) => isCineSeries(s));
    if (anyImages)
        set.add('dicom');
    if (anyRaster) {
        set.add('jpeg');
        set.add('png');
    }
    if (anyCine) {
        set.add('mp4');
        set.add('avi');
    }
    if (reportCount > 0)
        set.add('pdf');
    return FORMAT_ORDER.filter((f) => set.has(f));
}
